/**
 * Import GTFS přes web worker (hlavní vlákno se neseká) s fallbackem
 * na přímé zpracování tam, kde worker není (testy, stará prostředí).
 */
import type { GtfsImportResult } from './gtfs';
import { parseGtfsZip } from './gtfs';
import { parseGtfsZipFiltered, type GtfsBbox } from './gtfsLarge';
import { importCzMerged } from './gtfsCz';

export interface GtfsImportOptions {
  bbox: GtfsBbox | null;
  serviceDate: string;
  maxLines?: number;
}

type WorkerMsg = { progress?: string; ok?: boolean; result?: GtfsImportResult; error?: string };

function runWorker(
  payload: unknown,
  transfer: Transferable[],
  onProgress?: (t: string) => void,
): Promise<GtfsImportResult> {
  return new Promise((resolve, reject) => {
    const worker = new Worker(new URL('../workers/gtfsWorker.ts', import.meta.url), { type: 'module' });
    const done = (fn: () => void): void => {
      worker.terminate();
      fn();
    };
    worker.onmessage = (e: MessageEvent<WorkerMsg>) => {
      if (e.data.progress) {
        onProgress?.(e.data.progress);
        return;
      }
      if (e.data.ok && e.data.result) done(() => resolve(e.data.result!));
      else done(() => reject(new Error(e.data.error ?? 'GTFS se nepodařilo zpracovat.')));
    };
    worker.onerror = () => done(() => reject(new Error('Zpracování GTFS na pozadí selhalo.')));
    worker.postMessage(payload, transfer);
  });
}

export function importGtfsBytes(bytes: Uint8Array, opts: GtfsImportOptions): Promise<GtfsImportResult> {
  if (typeof Worker === 'undefined') {
    return opts.bbox
      ? parseGtfsZipFiltered(bytes, { bbox: opts.bbox, serviceDate: opts.serviceDate, maxLines: opts.maxLines })
      : Promise.resolve(parseGtfsZip(bytes, { serviceDate: opts.serviceDate, maxLines: opts.maxLines }));
  }
  // Buffer předáváme transferem — bez kopie desítek MB.
  return runWorker({ task: 'zip', bytes, bbox: opts.bbox, serviceDate: opts.serviceDate, maxLines: opts.maxLines }, [bytes.buffer]);
}

/** Celostátní dataset ČR — stahování i streamování běží ve workeru. */
export function importGtfsCz(
  bbox: GtfsBbox,
  serviceDate: string,
  onProgress?: (t: string) => void,
): Promise<GtfsImportResult> {
  if (typeof Worker === 'undefined') {
    return importCzMerged({ bbox, serviceDate, onProgress });
  }
  return runWorker({ task: 'cz', bbox, serviceDate }, [], onProgress);
}
